import * as React from "react";

import { Image, Box, Button, FileInput, Stack, Text, Pagination } from "grommet";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import StyledModal from "./StyledModel";
import CustomButton from "./CustomButton";
import { ContentViewModel } from "../content/ContentStore";

const pencil = <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>;
const xmark = <FontAwesomeIcon icon={["fad", "xmark"]}></FontAwesomeIcon>;

export const ContentPicker = ({
    width,
    label,
    uri,
    options,
    optionWidth,
    optionHeight,
    optionsPageSize,
    onSelect,
    suggestedSize,
    disabled,
    children,
    maxWidth,
}: {
    width: string;
    label: string;
    uri: string;
    options: ContentViewModel[];
    optionWidth: string;
    optionHeight: string;
    optionsPageSize: number;
    onSelect: Function;
    suggestedSize?: string;
    disabled?: boolean;
    children?: React.ReactNode;
    maxWidth?: number;
}) => {
    React.useEffect(() => {
        if (uri) {
            setConfirmed(false);
            setImage("");
        }
    }, [uri]);

    React.useEffect(() => {
        doPage({ startIndex: 0, endIndex: optionsPageSize, page: 1 });
    }, [options]);

    const [image, setImage] = React.useState("");
    const [showFileUpload, setShowFileUpload] = React.useState(false);
    const [confirmed, setConfirmed] = React.useState(false);

    const [pagedOptions, setPagedOptions] = React.useState((options || []).slice(0, optionsPageSize));

    const allowUpload = true;

    const confirm = () => {
        onSelect({ content: image });
        setShowFileUpload(false);
        setConfirmed(true);
    };

    const openFileUpload = () => {
        setConfirmed(false);
        setImage("");
        setShowFileUpload(true);
    };

    const chooseOption = (option) => () => {
        onSelect({ uri: option });
        setShowFileUpload(false);
        setConfirmed(true);
    };

    const [page, setPage] = React.useState(1);

    const doPage = ({ startIndex, endIndex, page }) => {
        setPagedOptions(options.slice(startIndex, endIndex));
        setPage(page);
    };

    const cancel = () => {
        setShowFileUpload(false);
        setImage("");
    };

    const clear = () => {
        onSelect({ uri: "", content: "" });
        setShowFileUpload(false);
        setConfirmed(true);
    };

    return (
        <>
            <Text>
                {label}
                {(uri || image) && (
                    <>
                        <Button icon={pencil} onClick={openFileUpload} disabled={disabled}></Button>
                        <Button icon={xmark} onClick={clear} disabled={disabled}></Button>
                        {children}
                    </>
                )}
            </Text>
            <Box>
                <Box>
                    {!uri && !confirmed && (
                        <Box pad="small">
                            {/* @ts-ignore */}
                            <CustomButton primary label="Choose Image" icon={<FontAwesomeIcon size="xl" icon={["fad", "image"]}></FontAwesomeIcon>} onClick={openFileUpload} disabled={disabled}></CustomButton>
                        </Box>
                    )}
                    {uri && <Image src={uri + "?ts=" + Math.random()} alt="" width={width} fit="cover" />}
                    {image && <Image src={image} alt="" width={width} fit="cover" />}
                </Box>

                <StyledModal isOpen={showFileUpload}>
                    <Box gap="small">
                        <Box>
                            <Text size="xsmall">Current</Text>
                            <Image src={uri} alt="" width={width} fit="cover" />
                        </Box>
                        {allowUpload && (
                            <FileInput
                                accept="image/*"
                                name="file"
                                onChange={(event) => {
                                    if (event) {
                                        setImage("");
                                        const fileList = event.target.files;
                                        if (fileList && fileList.length > 0) {
                                            const file = fileList[0];
                                            const reader = new FileReader();

                                            reader.addEventListener(
                                                "load",
                                                (e) => {
                                                    if (e.target && e.target.result) {
                                                        var img = document.createElement("img");
                                                        img.onload = function (event) {
                                                            var resizeWidth = img.width;
                                                            var resizeHeight = img.height;

                                                            if (maxWidth && resizeWidth > maxWidth) {
                                                                resizeHeight = resizeHeight * (maxWidth / resizeWidth);
                                                                resizeWidth = maxWidth;
                                                            }

                                                            var canvas = document.createElement("canvas");
                                                            canvas.width = resizeWidth;
                                                            canvas.height = resizeHeight;
                                                            var ctx = canvas.getContext("2d");
                                                            if (ctx) {
                                                                ctx.drawImage(img, 0, 0, resizeWidth, resizeHeight);

                                                                var dataurl = canvas.toDataURL(file.type);
                                                                setImage(dataurl as string);
                                                            }
                                                        };
                                                        img.src = e.target.result as string;
                                                    }
                                                },
                                                false
                                            );

                                            if (file) {
                                                reader.readAsDataURL(file);
                                            }
                                        }
                                    }
                                }}
                            />
                        )}
                        {suggestedSize && <Text size="xsmall">Suggested size {suggestedSize}</Text>}
                        {image && (
                            <Box>
                                <Text size="xsmall">New</Text>
                                <Image src={image} alt="" width={width} fit="cover" />
                            </Box>
                        )}
                        {options && !image && (
                            <>
                                <Box wrap={true} direction="row" gap="small" style={{ maxWidth: "400px", minHeight: "300px" }} align="center" justify="center" fill>
                                    {pagedOptions.map((option, index) => (
                                        <Image src={option.thumbURI} key={index} width={optionWidth} height={optionHeight} onClick={chooseOption(option.imageURI)}></Image>
                                    ))}
                                </Box>
                                <Pagination step={optionsPageSize} numberItems={options.length} onChange={doPage} page={page} />
                            </>
                        )}
                    </Box>
                    <Box direction="row" gap="small" justify="around" pad={{ top: "medium" }}>
                        <Button label="Cancel" onClick={cancel}></Button>
                        <Button primary label="Confirm" onClick={confirm}></Button>
                    </Box>
                </StyledModal>
            </Box>
        </>
    );
};
